import { clearExternalAccess, getCrmToken, isTokenExpired } from './externalAccess';

let redirectingToLogin = false;

// Called from every request path (and the build socket). Parallel failing requests would
// otherwise each navigate, so only the first one redirects.
export function handleSessionExpired() {
    if (redirectingToLogin || window.location.pathname === '/login') return;
    redirectingToLogin = true;
    clearExternalAccess();
    const next = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/login?reason=expired&next=${encodeURIComponent(next)}`);
}

function sessionExpiredError() {
    const error = new Error('Session expired');
    error.status = 401;
    error.code = 'session_expired';
    return error;
}

async function authorizedFetch(url, options = {}) {
    const token = getCrmToken();
    const expired = Boolean(token) && isTokenExpired(token);

    // Fail before the network call: the server would answer 401 anyway, and an expired token
    // should never be sent to the CRM login endpoint on the login page.
    if (expired && window.location.pathname !== '/login') {
        handleSessionExpired();
        throw sessionExpiredError();
    }

    const headers = new Headers(options.headers || {});
    if (token && !expired && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) handleSessionExpired();
    return response;
}

export async function fetchJson(url, options = {}) {
    const response = await authorizedFetch(url, options);
    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await response.json() : await response.text();

    if (!response.ok) {
        const detail = payload?.detail;
        const error = new Error(
            typeof payload === 'string'
                ? payload
                : (typeof detail === 'string' ? detail : payload?.message) || response.statusText
        );
        error.status = response.status;
        error.code = payload?.code;
        error.payload = payload;
        throw error;
    }

    return payload;
}

export async function downloadAuthenticatedFile(url, filename) {
    const response = await authorizedFetch(url);
    if (!response.ok) {
        const error = new Error(response.statusText || 'Download failed');
        error.status = response.status;
        throw error;
    }

    const objectUrl = window.URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
}

export function getFriendlyErrorMessage(error, messages) {
    if (error?.status === 401 || error?.status === 403) {
        return messages.auth;
    }

    if (error?.status === 404) {
        return messages.notFound || messages.generic;
    }

    if (error?.status === 409) {
        return messages.conflict || messages.generic;
    }

    if (error?.status >= 500) {
        return messages.server || messages.generic;
    }

    if (error?.message?.includes('Failed to fetch')) {
        return messages.network;
    }

    return messages.generic;
}
